import type { AdminVehicleCatalogMakeDetailDto } from '@carcheck/shared';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CatalogStatusChip, MakeInitials } from './CatalogChips';

/**
 * Drawer header (AUT-437), after `vehicle-catalog-make.html`: initials, name,
 * status / flag / source chips and «не видна в приложении» when the make is
 * active but has no active model. No technical id (a correction to the mockup).
 */
export const MakeDrawerHeader = ({
  make,
  notVisible,
  onClose,
}: {
  make: AdminVehicleCatalogMakeDetailDto;
  notVisible: boolean;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        p: '18px 24px',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <MakeInitials name={make.name} size={48} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h2" component="h2" sx={{ fontSize: 20, fontWeight: 700 }} noWrap>
          {make.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
          <CatalogStatusChip status={make.status} />
          {make.popular ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t('vehicleCatalog.flags.popular')}
            />
          ) : null}
          {make.onSale ? (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={t('vehicleCatalog.flags.onSale')}
            />
          ) : null}
          {notVisible ? (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={t('vehicleCatalog.drawer.notVisibleChip')}
            />
          ) : null}
          <Chip size="small" variant="outlined" label={t(`vehicleCatalog.source.${make.source}`)} />
        </Box>
      </Box>
      <IconButton aria-label={t('vehicleCatalog.drawer.close')} onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </Box>
  );
};
